import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

/**
 * `useState` whose value is persisted to `sessionStorage` and restored
 * SYNCHRONOUSLY on the next mount.
 *
 * The chat module pane unmounts a module when you switch away and cold-mounts it
 * on return, so a module's view position (active tab, open board, current
 * folder, anchor date) used to reset to its default and then re-derive after
 * paint — the "weird snapshot then it loads in" beat. Backing that position with
 * this hook makes the FIRST frame of re-entry already correct (the lazy
 * initializer reads storage during the initial render, exactly like a plain
 * `useState(() => …)`), with no post-paint correction; the module then refreshes
 * its data silently. Pairs with per-module data caches (TanStack query cache /
 * the email inbox sessionStorage cache) which handle the instant data paint.
 *
 * Rules honored:
 * - Keys MUST be `xavior:`-prefixed (storage namespacing) — callers build them.
 * - `storageKey === null` disables persistence (behaves as plain `useState`) for
 *   the window before a module instance id is known. Keep the key STABLE for the
 *   lifetime of a mount — the value is read once, on first render.
 * - The write-back is a `sessionStorage.setItem`, never a window listener, so it
 *   does not perturb effect-registration ledgers (e.g. EmailPanel's).
 */
/**
 * Read + validate a persisted value (the lazy-initializer half of the hook).
 * Exposed standalone for modules that keep a plain `useState` — those whose
 * setter is referenced in `useEffect`/`useCallback` dependency arrays, since
 * only a DIRECT `useState` setter is recognized as referentially stable by
 * `react-hooks/exhaustive-deps` (the hook's returned setter would spuriously
 * warn). Pair it with `writeSessionPersisted` in a write-back effect.
 */
export function readSessionPersisted<T>(
  storageKey: string | null,
  defaultValue: T | (() => T),
  validate?: (value: unknown) => value is T,
): T {
  const fallback = (): T =>
    typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
  if (!storageKey || typeof window === 'undefined') return fallback();
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw === null) return fallback();
    const parsed = JSON.parse(raw) as unknown;
    if (validate && !validate(parsed)) return fallback();
    return parsed as T;
  } catch {
    return fallback();
  }
}

/** Best-effort write-back half. No-op when the key is null or storage is absent/full. */
export function writeSessionPersisted<T>(storageKey: string | null, value: T): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    /* quota exceeded / storage disabled — best-effort, non-fatal */
  }
}

export function useSessionPersistedState<T>(
  storageKey: string | null,
  defaultValue: T | (() => T),
  opts?: {
    /** Reject a malformed / mistyped stored value (falls back to the default). */
    validate?: (value: unknown) => value is T;
  },
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() =>
    readSessionPersisted(storageKey, defaultValue, opts?.validate),
  );
  useEffect(() => {
    writeSessionPersisted(storageKey, value);
  }, [storageKey, value]);
  return [value, setValue];
}
